import { Link, Outlet, createFileRoute, notFound } from '@tanstack/react-router'

import { loungeMembersQueryOptions, loungeQueryOptions, useLounge } from '@/features/lounges/api'
import { nooksQueryOptions, useLoungeStream } from '@/features/nooks/api'
import { usePresenceStream } from '@/features/presence/api'
import { useVoicePresence } from '@/features/voice/api'
import { usePushToTalk, useVoice } from '@/features/voice/useVoice'

/**
 * One lounge.
 *
 * Thinner than it was: the sidebar moved up into `AppShell` in Milestone 6,
 * because the lounge's own name now carries the switcher and the sidebar has to
 * survive being rendered above the route that knows which lounge it is. What is
 * left here is the main column, the lounge's socket subscriptions, and the
 * loader that warms all three caches the sidebar reads.
 *
 * The `<Outlet/>` holds both the nook view and the lounge's modal routes — Radix
 * portals a dialog out of the DOM, so one outlet serves both without the modals
 * inheriting the column's layout.
 */
export const Route = createFileRoute('/_app/l/$loungeSlug')({
  loader: async ({ context: { queryClient }, params: { loungeSlug } }) => {
    const { lounge } = await queryClient.ensureQueryData(loungeQueryOptions(loungeSlug))

    // The server returns null both for "no such slug" and "not yours to see" —
    // deliberately indistinguishable, so this is the only branch there is.
    if (!lounge) throw notFound()

    await Promise.all([
      queryClient.ensureQueryData(loungeMembersQueryOptions(lounge.id)),
      queryClient.ensureQueryData(nooksQueryOptions(lounge.id)),
    ])
    return { lounge }
  },
  component: LoungeLayout,
  notFoundComponent: LoungeNotFound,
})

function LoungeLayout() {
  const { loungeSlug } = Route.useParams()
  const { data } = useLounge(loungeSlug)
  const lounge = data?.lounge

  if (!lounge) return null

  return (
    <>
      <main className="lounge-main">
        <Outlet />
      </main>

      <LoungeRealtime loungeId={lounge.id} />
    </>
  )
}

/**
 * Holds the lounge's socket subscriptions and renders nothing.
 *
 * Its own component so that a re-render of the column cannot churn them, and so
 * the layout above reads as layout.
 */
function LoungeRealtime({ loungeId }: { loungeId: string }) {
  const voice = useVoice()

  useLoungeStream(loungeId)
  // Multiplexed over the same socket. Separate from the nook stream because
  // presence is ephemeral and the nook list is cached server state — the same
  // split messages and typing already use.
  useVoicePresence(loungeId)
  // A third stream on the *same* Redis channel: the bus hands back another
  // receiver on the sender that is already there, so this costs no new
  // subscription on the server side at all.
  usePresenceStream(loungeId)
  // Push-to-talk is a property of the connection, not of whichever widget
  // happens to be on screen — it belongs beside the room's other long-lived
  // concerns rather than inside the control cluster, which unmounts every time
  // that surface moves.
  usePushToTalk(voice.nookId !== null)
  return null
}

function LoungeNotFound() {
  return (
    <div className="pane-column">
      <h1 className="pane-title">No such lounge</h1>
      <p className="muted">
        This lounge does not exist, or you are not a member of it. If someone invited you, follow
        their link instead.
      </p>
      <p>
        <Link to="/">Back to your lounges</Link>
      </p>
    </div>
  )
}
